import React, { useMemo, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { observer } from "mobx-react-lite";
import { createPlugin, indexPlugins, statusPlugin, updatePlugin, updatePluginSetting, visibilityPlugin } from "../http/PluginAPI";
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import BreadCrumbs from "../components/Breadcrumbs";
import { PLUGIN_DETAILS_ROUTE } from "../utils/consts";
import { getApiErrorMessage } from "../http/apiError";
import { useAsyncData } from "../hooks/useAsyncData";
import PluginsTable from "./plugins/components/PluginsTable";
import { AddPluginModal, EditPluginModal } from "./plugins/components/PluginFormModal";
import { useSnackbar } from "../components/snackbar/SnackbarProvider";

const defaultPluginForm = {
    provider: 1,
    name: '',
    version: '',
    file: null,
    visible: true,
};

const getProviderName = (plugin) => (
    plugin.provider ?? plugin.provider_name ?? plugin.source ?? `Источник ${plugin.provider_id ?? '—'}`
);

const Plugins = observer(() => {
    const navigate = useNavigate();
    const snackbar = useSnackbar();
    const [openSettings, setOpenSettings] = useState(false);
    const [openAddPlugin, setOpenAddPlugin] = useState(false);
    const {
        data: plugins = [],
        setData: setPlugins,
        reload: loadPlugins,
        loading,
        error,
    } = useAsyncData(indexPlugins, {
        initialData: [],
        errorMessage: 'Ошибка загрузки плагинов',
    });

    const [selectedPlugin, setSelectedPlugin] = useState(null);
    const [pluginForm, setPluginForm] = useState(defaultPluginForm);
    const [updatingPluginIds, setUpdatingPluginIds] = useState([]);

    const stats = useMemo(() => {
        const providers = new Set();
        let enabled = 0;
        let disabled = 0;
        let visible = 0;
        let hidden = 0;

        plugins.forEach((plugin) => {
            providers.add(getProviderName(plugin));
            if (Boolean(plugin.status)) enabled += 1;
            else disabled += 1;

            if (Boolean(plugin.visible ?? plugin.is_visible ?? plugin.player_visible ?? true)) visible += 1;
            else hidden += 1;
        });

        return {
            total: plugins.length,
            enabled,
            disabled,
            visible,
            hidden,
            providers: providers.size,
        };
    }, [plugins]);

    const updateForm = (field, value) => {
        setPluginForm(prev => ({ ...prev, [field]: value }));
    };

    const openAddModal = () => {
        setPluginForm(defaultPluginForm);
        setOpenAddPlugin(true);
    };

    const closeAddModal = () => setOpenAddPlugin(false);
    const closeSettingsModal = () => setOpenSettings(false);

    const openSettingsModal = (plugin) => {
        setSelectedPlugin(plugin);
        setPluginForm({
            provider: plugin.provider_id ?? 1,
            name: plugin.plugin_name ?? plugin.name ?? '',
            version: plugin.version ?? '',
            file: null,
            visible: Boolean(plugin.visible ?? plugin.is_visible ?? plugin.player_visible ?? true),
        });
        setOpenSettings(true);
    };

    const createFormData = (includePluginId = false) => {
        const formData = new FormData();
        if (includePluginId && selectedPlugin) formData.append('plugin_id', selectedPlugin.plugin_id);
        formData.append('provider', pluginForm.provider);
        formData.append('name', pluginForm.name);
        formData.append('version', pluginForm.version);
        if (pluginForm.file) formData.append('file', pluginForm.file);
        formData.append('visible', pluginForm.visible ? '1' : '0');
        return formData;
    };

    const handleCreatePlugin = async () => {
        try {
            await createPlugin(createFormData());
            closeAddModal();
            await loadPlugins();
            snackbar.success('Плагин добавлен');
        } catch (e) {
            snackbar.error(getApiErrorMessage(e, 'Не удалось добавить плагин'));
        }
    };

    const handleUpdatePluginSettings = async () => {
        if (!selectedPlugin) return;

        try {
            await updatePluginSetting(createFormData(true));
            closeSettingsModal();
            await loadPlugins();
            snackbar.success('Настройки плагина сохранены');
        } catch (e) {
            snackbar.error(getApiErrorMessage(e, 'Не удалось сохранить настройки'));
        }
    };

    const handleToggleStatus = async (status, pluginId) => {
        setPlugins(prev => prev.map(item => item.plugin_id === pluginId ? { ...item, status } : item));
        try {
            await statusPlugin(pluginId, status);
            snackbar.success(status ? 'Плагин включён' : 'Плагин выключен');
        } catch (e) {
            snackbar.error(getApiErrorMessage(e, 'Не удалось изменить статус'));
            await loadPlugins();
        }
    };


    const handleToggleVisibility = async (visible, pluginId) => {
        setPlugins(prev => prev.map(item => item.plugin_id === pluginId ? { ...item, visible } : item));
        try {
            await visibilityPlugin(pluginId, visible);
            snackbar.success(visible ? 'Плагин виден игрокам' : 'Плагин скрыт от игроков');
        } catch (e) {
            snackbar.error(getApiErrorMessage(e, 'Не удалось изменить видимость'));
            await loadPlugins();
        }
    };

    const handleUpdatePlugin = async (pluginId) => {
        setUpdatingPluginIds(prev => [...prev, pluginId]);
        try {
            await updatePlugin(pluginId);
            await loadPlugins();
            snackbar.success('Обновление плагина запущено');
        } catch (e) {
            snackbar.error(getApiErrorMessage(e, 'Не удалось обновить плагин'));
        } finally {
            setUpdatingPluginIds(prev => prev.filter(id => id !== pluginId));
        }
    };

    const handleUpdateVisible = async (pluginIds) => {
        if (!pluginIds.length) return;

        let successCount = 0;
        let failedCount = 0;

        for (const pluginId of pluginIds) {
            setUpdatingPluginIds(prev => [...new Set([...prev, pluginId])]);
            try {
                await updatePlugin(pluginId);
                successCount += 1;
            } catch (e) {
                failedCount += 1;
            } finally {
                setUpdatingPluginIds(prev => prev.filter(id => id !== pluginId));
            }
        }

        await loadPlugins();

        if (failedCount > 0) {
            snackbar.warning(`Обновлено: ${successCount}, с ошибкой: ${failedCount}`);
        } else {
            snackbar.success(`Обновлено плагинов: ${successCount}`);
        }
    };

    return (
        <>
            <Container maxWidth="xl" sx={{ pb: 4 }}>
                <Box sx={{ width: '100%', mt: 3 }}>
                    <BreadCrumbs hrefs="" name="Библиотека плагинов" />
                </Box>

                <PluginsTable
                    plugins={plugins}
                    stats={stats}
                    loading={loading}
                    error={error}
                    updatingPluginIds={updatingPluginIds}
                    onReload={loadPlugins}
                    onAddPlugin={openAddModal}
                    onOpenDetails={(pluginId) => navigate(`${PLUGIN_DETAILS_ROUTE}/${pluginId}`)}
                    onToggleStatus={handleToggleStatus}
                    onToggleVisibility={handleToggleVisibility}
                    onOpenSettings={openSettingsModal}
                    onUpdatePlugin={handleUpdatePlugin}
                    onUpdateVisible={handleUpdateVisible}
                />
            </Container>

            <AddPluginModal
                open={openAddPlugin}
                onClose={closeAddModal}
                form={pluginForm}
                onFormChange={updateForm}
                onSubmit={handleCreatePlugin}
            />
            <EditPluginModal
                open={openSettings}
                onClose={closeSettingsModal}
                form={pluginForm}
                onFormChange={updateForm}
                onSubmit={handleUpdatePluginSettings}
            />
        </>
    );
});

export default Plugins;
